import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { GripVertical, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useDagStore } from "@/app/hooks/useDagStore";
import { omitDefaultSearchParams, SEARCH_DEFAULTS } from "@/app/routes/index.route";
import { calculateTeamCounts } from "@/app/utils/teamFilters";

interface SortableTeamItemProps {
  team: string;
  isChecked: boolean;
  onToggle: () => void;
  count?: number;
  isDragging?: boolean;
}

function SortableTeamItem({
  team,
  isChecked,
  onToggle,
  count,
  isDragging = false,
}: SortableTeamItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: team });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
  };

  const handleTeamClick = () => {
    const teamId = `team-${team.toLowerCase().replace(/\s+/g, "-")}`;
    const element = document.getElementById(teamId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-2 p-1 rounded hover:bg-stone-50 transition-colors"
    >
      <button
        type="button"
        className="text-stone-400 hover:text-stone-600 cursor-grab active:cursor-grabbing p-0.5"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${team} to reorder`}
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <div className="flex items-center flex-1">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onToggle}
          className="h-4 w-4 text-blue-600 rounded border-stone-300 focus:ring-blue-500"
          aria-label={`Filter by ${team} team`}
        />
        <button
          type="button"
          onClick={handleTeamClick}
          className="ml-2 text-sm text-stone-700 hover:text-blue-600 hover:underline transition-colors text-left flex-1"
          title={`Navigate to ${team} section`}
        >
          {team} {count !== undefined && `(${count})`}
        </button>
      </div>
    </div>
  );
}

interface DraggableTeamListProps {
  className?: string;
}

export function DraggableTeamList({ className = "" }: DraggableTeamListProps) {
  const router = useRouterState();
  const isDashboard = router.location.pathname === "/";

  // Always call hooks unconditionally
  const search = useSearch({ strict: false });
  const teams = useDagStore(useShallow((state) => state.allTeams));
  const teamMembers = useDagStore(useShallow((state) => state.teamMembersFiltered));
  const storeExcludedTeams = useDagStore(useShallow((state) => state.excludedTeams));
  const teamOrder = useDagStore(useShallow((state) => state.teamOrder));
  const storeToggleTeam = useDagStore(useShallow((state) => state.toggleTeam));
  const setTeamOrder = useDagStore(useShallow((state) => state.setTeamOrder));
  const initializeRandomTeamOrder = useDagStore(
    useShallow((state) => state.initializeRandomTeamOrder),
  );
  const storeResetFilters = useDagStore(useShallow((state) => state.resetFilters));
  const navigate = useNavigate({ from: "/" });

  const [activeTeam, setActiveTeam] = useState<string | null>(null);

  // Get excludedTeams from URL if on dashboard, otherwise from store
  const excludedTeamsArray = isDashboard
    ? (search?.excludedTeams ?? SEARCH_DEFAULTS.excludedTeams)
    : Array.from(storeExcludedTeams);
  const excludedTeams = useMemo(() => new Set(excludedTeamsArray), [excludedTeamsArray]);

  const onTeamToggle = (team: string) => {
    if (isDashboard && search) {
      const newExcludedTeams = new Set(excludedTeamsArray);
      if (newExcludedTeams.has(team)) {
        newExcludedTeams.delete(team);
      } else {
        newExcludedTeams.add(team);
      }
      void navigate({
        search: omitDefaultSearchParams({
          ...search,
          excludedTeams: Array.from(newExcludedTeams),
        }),
      });
    } else {
      storeToggleTeam(team);
    }
  };

  const resetFilters = () => {
    if (isDashboard && search) {
      const { memberId } = search;
      const resetParams = memberId ? { ...SEARCH_DEFAULTS, memberId } : SEARCH_DEFAULTS;
      void navigate({ search: omitDefaultSearchParams(resetParams) });
    } else {
      storeResetFilters();
    }
  };

  const teamCounts = useMemo(() => calculateTeamCounts(teamMembers, teams), [teamMembers, teams]);

  // Initialize team order if not already set
  useEffect(() => {
    if (teams.length > 0 && teamOrder.length === 0) {
      initializeRandomTeamOrder();
    }
  }, [teams.length, teamOrder.length, initializeRandomTeamOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveTeam(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = teams.indexOf(String(active.id));
      const newIndex = teams.indexOf(String(over?.id));

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(teams, oldIndex, newIndex);
        setTeamOrder(newOrder);
      }
    }

    setActiveTeam(null);
  };

  if (teams.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      <h3 className="text-sm font-semibold text-stone-700 mb-2">Teams</h3>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={teams} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {teams.map((team) => (
              <SortableTeamItem
                key={team}
                team={team}
                isChecked={!excludedTeams.has(team)}
                onToggle={() => onTeamToggle(team)}
                count={teamCounts[team]}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeTeam ? (
            <SortableTeamItem
              team={activeTeam}
              isChecked={!excludedTeams.has(activeTeam)}
              onToggle={() => undefined}
              count={teamCounts[activeTeam]}
              isDragging={true}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Reset Filters Button */}
      <button
        type="button"
        onClick={resetFilters}
        className="w-full mt-3 px-3 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 border border-stone-200 rounded-lg transition-colors flex items-center justify-center gap-2"
        title="Reset all filters"
      >
        <RotateCcw className="w-4 h-4" />
        Reset Filters
      </button>
    </div>
  );
}
